import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  FormAlert,
  FormDialog,
  FormCard,
  FormSection,
  FormGrid,
  FormField,
} from "@NP-Webber/crm-kit";
import { Banner, DataTable, exportRows, QuickFilters, Tag, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import type {
  CkColSet,
  CkColumnType,
  CkDataColumn,
  CkMenuItem,
  CkQuickFilterItem,
  CkSelection,
  CkTableView,
  CkTone,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  customersApi,
  type Customer,
  type CustomerListCatalogColumn,
  type CustomerQuickKey,
  type CustomersTablePage,
} from "../../api/customersApi";
import { toClientApiError } from "../../api/httpClient";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { UiStateMessage } from "../../engines";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { LookupSelect } from "../../ui/LookupSelect";
import {
  lookupLabelMap,
  useLookupOptions,
} from "../../ui/useLookupOptions";
import { CustomerCard } from "./CustomerCard";
import { DataEntryRecommendation } from "../../next/ai/dataEntry/DataEntryRecommendation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuickCreate } from "../../next/quickCreate";
import { useScreenSettings } from "../../next/related/relatedStore";
import { EVENTS, onEvent } from "../../next/events";
import { useShell } from "../../next/shell/ShellContext";
import { useServerTable, type EnumColumn } from "../../next/useServerTable";
import {
  CustomerProductTags,
  parseActiveProducts,
} from "./customerProductTags";

function canCancelCustomerStatus(status: unknown): boolean {
  return status === "active" || status === "frozen";
}

const STATUS_META: Record<string, { label: string; tone: CkTone }> = {
  active: { label: "פעיל", tone: "success" },
  frozen: { label: "מוקפא", tone: "warning" },
  cancelled: { label: "מבוטל", tone: "danger" },
};

function statusLabel(status: string): string {
  return STATUS_META[status]?.label ?? STATUS_META.cancelled!.label;
}

const PRODUCT_STATUS: Record<string, string> = { active: "פעיל", cancelled: "בוטל" };

/** the quick cards (Turbo: the alfon's stat cards) — one at a time; counts follow every other filter */
type Quick = "" | CustomerQuickKey;
const QUICK_KEYS: CustomerQuickKey[] = ["active", "frozen", "cancelled", "debt", "mandateExpiring", "atRisk", "newThisMonth"];
const isQuick = (v: string | null | undefined): v is Quick => v === "" || (QUICK_KEYS as string[]).includes(v ?? "");
const QUICK_META: Record<CustomerQuickKey, { label: string; icon: string; tone: CkTone; warn?: boolean }> = {
  active: { label: "פעילים", icon: "check", tone: "success" },
  frozen: { label: "מוקפאים", icon: "pause", tone: "warning" },
  cancelled: { label: "מבוטלים", icon: "x", tone: "danger" },
  debt: { label: "עם חוב פתוח", icon: "coin", tone: "danger", warn: true },
  mandateExpiring: { label: "הרשאה לפני פקיעה", icon: "clock", tone: "warning" },
  atRisk: { label: "בסיכון", icon: "alert", tone: "danger", warn: true },
  newThisMonth: { label: "הצטרפו החודש", icon: "sparkle", tone: "info" },
};
const STATUS_QUICK = new Set<Quick>(["active", "frozen", "cancelled"]);

const TABLE_KEY = "tb.customers.list";
/** money columns: agorot in the DTO, shekels in the table (filters, footer, export) */
const MONEY_KEYS = new Set(["monthlyAmount", "totalPaid", "balanceDue"]);
const LTR_KEYS = new Set(["phone", "contactPhone", "email", "payLast4", "regulatoryId"]);
const COPY_KEYS = new Set(["phone", "contactPhone", "email"]);
/** enum columns whose values are lookup keys → the lookup list that labels them */
const LOOKUP_OF: Record<string, string> = {
  cityKey: "cities",
  billingMethodKey: "billing_methods",
  cancelReasonKey: "cancel_reasons",
  sourceKey: "lead_sources",
  commPrefKey: "comm_prefs",
  lastChargeStatusKey: "charge_statuses",
};
const LOOKUP_LISTS = Array.from(new Set(Object.values(LOOKUP_OF)));

const shekels = (agorot: unknown) => (agorot === null || agorot === undefined || agorot === "" ? null : Number(agorot) / 100);
const fmtNum = (n: number) => n.toLocaleString("he-IL");

function columnType(col: CustomerListCatalogColumn): CkColumnType {
  if (MONEY_KEYS.has(col.fieldKey)) return "money";
  if (col.type === "number") return "number";
  if (col.type === "date" || col.type === "datetime") return "date";
  if (col.type === "boolean") return "bool";
  if (col.type === "enum") return "enum";
  return "text";
}

/** a column's raw value on the row: a DTO field, a custom field, or a product status (prod:<key>) */
function rawValue(row: Customer, key: string): unknown {
  if (key.startsWith("prod:")) return row.products?.[key.slice(5)] || null;
  if (key in row) return (row as unknown as Record<string, unknown>)[key];
  return row.attributes?.[key] ?? null;
}

const displayName = (row: Customer) => row.communityName?.trim() || row.name?.trim() || "—";

/** one request per id, counting what failed (bulk status changes / cancellations) */
async function runEach(ids: string[], fn: (id: string) => Promise<unknown>) {
  let ok = 0;
  let failed = 0;
  for (const id of ids) {
    try {
      await fn(id);
      ok += 1;
    } catch {
      failed += 1;
    }
  }
  return { ok, failed };
}

type ImportPreview = Awaited<ReturnType<typeof customersApi.previewImportFile>>;

/** URL deep links (Turbo): ?quick=<card>, ?status=<status> (older links / mailing) and ?vf_<column>=a,b — applied once */
function initialState(p: URLSearchParams): { quick: Quick; filters: Record<string, { mode: "values"; values: string[] }> } {
  const q = p.get("quick");
  const status = p.get("status");
  const quick: Quick = isQuick(q) ? q : status === "active" || status === "frozen" ? status : status === "cancelled" || status === "archived" ? "cancelled" : "";
  const filters: Record<string, { mode: "values"; values: string[] }> = {};
  p.forEach((v, k) => {
    if (k.startsWith("vf_") && v) filters[k.slice(3)] = { mode: "values", values: v.split(",") };
  });
  return { quick, filters };
}

/**
 * /customers — the customers list (Turbo DataTable anatomy, the alfon): quick cards, typed columns with server
 * column filters, column sets, views, row actions, bulk actions over the selection or all results, export, footer sums;
 * a row opens the customer card. Server mode — works with any number of customers.
 */
export function CustomersListPage() {
  const labels = useEntityLabels();
  const toast = useCkToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allowed } = useShell();
  const canArchive = allowed("customers.archive");
  const canUpdate = allowed("customers.update");
  const canMail = allowed("mailing.manage");
  const navigate = useNavigate();

  const [init] = useState(() => initialState(searchParams));
  const [quick, setQuick] = useState<Quick>(init.quick);
  const [forbidden, setForbidden] = useState(false);
  const [clearKey, setClearKey] = useState(0);
  const [boolKeys, setBoolKeys] = useState<string[]>([]);

  const [communityName, setCommunityName] = useState("");
  const [cityKey, setCityKey] = useState("");
  const [billingMethodKey, setBillingMethodKey] = useState("");
  const [joinDate, setJoinDate] = useState("");
  const [contactFirst, setContactFirst] = useState("");
  const [contactLast, setContactLast] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  /** the cancellation dialog: one row (row action) or many (bulk) */
  const [archiveTarget, setArchiveTarget] = useState<{ ids: string[]; label: string } | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveCancelReasonKey, setArchiveCancelReasonKey] = useState("");

  const { optionsByList } = useLookupOptions(LOOKUP_LISTS);
  const cityOptions = useMemo(
    () => optionsByList.cities ?? [],
    [optionsByList.cities],
  );
  const billingMethodOptions = useMemo(
    () => optionsByList.billing_methods ?? [],
    [optionsByList.billing_methods],
  );
  const cancelReasonOptions = useMemo(
    () => optionsByList.cancel_reasons ?? [],
    [optionsByList.cancel_reasons],
  );
  const lookupLabels = useMemo(
    () => Object.fromEntries(LOOKUP_LISTS.map((list) => [list, lookupLabelMap(optionsByList[list] ?? [])])),
    [optionsByList],
  );
  const lookupLabel = useCallback(
    (colKey: string, value: unknown) => {
      const key = value === null || value === undefined ? "" : String(value);
      if (!key) return "";
      return lookupLabels[LOOKUP_OF[colKey] ?? ""]?.[key] ?? key;
    },
    [lookupLabels],
  );

  const enums = useMemo<Record<string, EnumColumn>>(() => {
    const out: Record<string, EnumColumn> = {
      status: {
        label: (v) => STATUS_META[v]?.label ?? v,
        options: Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label })),
      },
    };
    for (const [colKey, list] of Object.entries(LOOKUP_OF)) {
      out[colKey] = {
        label: (v) => lookupLabel(colKey, v),
        options: (optionsByList[list] ?? []).map((o) => ({ value: o.key, label: o.labelHe })),
      };
    }
    return out;
  }, [lookupLabel, optionsByList]);

  const extra = useMemo(() => (quick ? { quick } : ({} as Record<string, string>)), [quick]);

  const table = useServerTable<Customer, CustomersTablePage>({
    load: (p) =>
      customersApi.table(p).catch((err: unknown) => {
        if (toClientApiError(err).status === 403) setForbidden(true);
        throw err;
      }),
    loadValues: (p) => customersApi.tableColumnValues(p),
    extra,
    enums,
    bools: boolKeys,
    defaultQuery: { filters: init.filters },
    errorText: `לא הצלחנו לטעון את ה${labels.customers}`,
  });
  const { query, setQuery, firstPage, data, reload } = table;

  // the deep-link params were applied once — drop them from the URL (?card= stays)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const drop = Array.from(next.keys()).filter((k) => k === "quick" || k === "status" || k.startsWith("vf_"));
    if (!drop.length) return;
    drop.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeQuick = (k: string) => {
    setQuick(k === "all" ? "" : isQuick(k) ? k : "");
    firstPage();
  };
  const afterChange = () => {
    setClearKey((k) => k + 1);
    reload();
  };

  // the column catalog (core, product subscriptions, custom fields) comes with the data; kept stable between pages
  const catalogJson = JSON.stringify(data?.catalog ?? null);
  const catalog = useMemo(() => (JSON.parse(catalogJson) as CustomersTablePage["catalog"] | null), [catalogJson]);
  const policyJson = JSON.stringify((data as { fieldPolicy?: Record<string, string> } | null)?.fieldPolicy ?? {});

  const openCustomer = useCallback((id: string) => {
    if (!id) return;
    setSelectedCustomerId(id);
    setCardOpen(true);
  }, []);

  const columns = useMemo<Array<CkDataColumn<Customer>>>(() => {
    const policy = JSON.parse(policyJson) as Record<string, string>;
    return (catalog?.columns ?? [])
      .filter((col) => col.fieldKey !== "logoUrl" && policy[col.fieldKey] !== "hide")
      .map((col): CkDataColumn<Customer> => {
        const key = col.fieldKey;
        const type: CkColumnType = key.startsWith("prod:") ? "enum" : columnType(col);
        const base: CkDataColumn<Customer> = {
          key,
          label: col.labelHe,
          type,
          sortable: true,
          ltr: LTR_KEYS.has(key),
          copy: COPY_KEYS.has(key),
          onCopied: COPY_KEYS.has(key) ? () => toast("הועתק") : undefined,
          sum: MONEY_KEYS.has(key),
          getValue: (r) => rawValue(r, key),
        };
        if (key === "name") {
          return {
            ...base,
            width: 220,
            getValue: (r) => displayName(r),
            render: (r) => {
              const community = r.communityName?.trim();
              return (
                <span className="customers-list__name">
                  <b>{displayName(r)}</b>
                  {community && r.name && community !== r.name.trim() ? <small> · {r.name}</small> : null}
                  {r.flagAtRisk ? (
                    <>
                      {" "}
                      <Tag tone="danger" icon="alert">
                        בסיכון
                      </Tag>
                    </>
                  ) : null}
                </span>
              );
            },
          };
        }
        if (key === "activeProducts") {
          return {
            ...base,
            type: "text",
            sortable: false,
            filterable: false,
            width: 280,
            getValue: (r) => parseActiveProducts(r.activeProducts).map((t) => t.labelHe).join(", "),
            render: (r) => <CustomerProductTags tags={parseActiveProducts(r.activeProducts)} />,
          };
        }
        if (key === "status") {
          return {
            ...base,
            getValue: (r) => statusLabel(r.status),
            render: (r) => <Tag tone={STATUS_META[r.status]?.tone ?? "danger"}>{statusLabel(r.status)}</Tag>,
          };
        }
        if (LOOKUP_OF[key]) return { ...base, getValue: (r) => lookupLabel(key, rawValue(r, key)) };
        if (key.startsWith("prod:")) return { ...base, getValue: (r) => PRODUCT_STATUS[String(rawValue(r, key) ?? "")] ?? "" };
        if (type === "money") return { ...base, getValue: (r) => shekels(rawValue(r, key)) };
        if (type === "bool") {
          return {
            ...base,
            getValue: (r) => {
              const v = rawValue(r, key);
              return v === null || v === undefined ? null : Boolean(v);
            },
          };
        }
        return base;
      });
  }, [catalog, policyJson, lookupLabel, toast]);

  const columnKeys = useMemo(() => new Set(columns.map((c) => c.key)), [columns]);
  const present = useCallback((keys: string[]) => keys.filter((k) => columnKeys.has(k)), [columnKeys]);
  const basicCols = present(catalog?.defaultVisibleKeys ?? ["name", "cityKey", "contactName", "contactPhone", "status", "joinDate"]);
  const productKeys = columns.map((c) => c.key).filter((k) => k.startsWith("prod:"));
  /** column sets — switch topic instead of scrolling sideways */
  const colSets: CkColSet[] = [
    { key: "basic", label: "בסיסי", cols: basicCols },
    { key: "contact", label: "פרטי קשר", cols: present(["name", "contactName", "contactPhone", "phone", "email", "address", "cityKey", "communityName", "generalCommunity"]) },
    {
      key: "billing",
      label: "גבייה",
      cols: present(["name", "billingMethodKey", "monthlyAmount", "billingDay", "mandateActive", "mandateExpiryOn", "payLast4", "lastChargeStatusKey", "lastChargeOn", "failedCharges"]),
    },
    { key: "money", label: "כספים", cols: present(["name", "totalPaid", "balanceDue", "lastPaymentDate", "daysSincePayment", "flagDebt", "flagAtRisk"]) },
    ...(productKeys.length ? [{ key: "products", label: "מנויים", cols: present(["name", "activeProducts", ...productKeys]) }] : []),
    { key: "status", label: "הצטרפות וביטול", cols: present(["name", "status", "joinDate", "tenureDays", "sourceKey", "cancelDate", "cancelReasonKey", "commPrefKey", "lastOutreachOn"]) },
  ].filter((s) => s.cols.length > 1);
  const hiddenByDefault = columns.map((c) => c.key).filter((k) => !basicCols.includes(k));
  // bool columns: their values tab shows כן / לא (known once the catalog arrived)
  const boolJson = JSON.stringify(columns.filter((c) => c.type === "bool").map((c) => c.key));
  useEffect(() => setBoolKeys(JSON.parse(boolJson) as string[]), [boolJson]);

  const builtinViews: CkTableView[] = [
    { id: "all", label: `כל ה${labels.customers}`, state: { query: { search: "", filters: {}, sort: null }, extra: "" } },
    { id: "active", label: "פעילים", state: { query: { search: "", filters: {}, sort: { key: "name", dir: 1 } }, extra: "active" } },
    { id: "debt", label: "חובות — מהגדול", state: { query: { search: "", filters: {}, sort: { key: "balanceDue", dir: -1 } }, extra: "debt" } },
    { id: "mandate", label: "הרשאות לפני פקיעה", state: { query: { search: "", filters: {}, sort: { key: "mandateExpiryOn", dir: 1 } }, extra: "mandateExpiring" } },
    { id: "recent", label: "עודכנו לאחרונה", state: { query: { search: "", filters: {}, sort: { key: "updatedAt", dir: -1 } }, extra: "" } },
  ];

  const qc = data?.quickCounts;
  const quickItems: CkQuickFilterItem[] = [
    { key: "all", label: `סה״כ ${labels.customers}`, icon: "users", tone: "primary", value: qc?.all },
    ...QUICK_KEYS.map((k) => ({ key: k, label: QUICK_META[k].label, icon: QUICK_META[k].icon, tone: QUICK_META[k].tone, warn: QUICK_META[k].warn, value: qc?.[k] })),
  ];
  const extraChips = quick ? [{ key: "quick", label: QUICK_META[quick].label, tone: "primary" as const, onRemove: () => changeQuick("all") }] : [];
  const footerTotals = data?.sums
    ? { totalPaid: shekels(data.sums.totalPaid), balanceDue: shekels(data.sums.balanceDue), monthlyAmount: shekels(data.sums.monthlyAmount) }
    : undefined;
  const filtering = Boolean(quick || query.search || Object.keys(query.filters).length);

  const handleArchiveConfirm = async () => {
    if (!archiveTarget || !archiveCancelReasonKey) {
      return;
    }
    setArchiveBusy(true);
    try {
      const r = await runEach(archiveTarget.ids, (id) =>
        customersApi.archive(id, { cancelReasonKey: archiveCancelReasonKey }),
      );
      if (archiveTarget.ids.length > 1) {
        if (r.failed) toast.error(`${fmtNum(r.ok)} בוטלו · ${fmtNum(r.failed)} לא בוטלו (כבר מבוטלים או שגיאה)`);
        else toast.success(`${fmtNum(r.ok)} ${labels.customers} בוטלו`);
      } else if (r.failed) {
        toast.error("הביטול נכשל");
      }
      setArchiveTarget(null);
      setArchiveCancelReasonKey("");
      afterChange();
    } finally {
      setArchiveBusy(false);
    }
  };

  /** bulk: active ⇄ frozen (cancelled ones are skipped by the server) */
  const setStatusMany = async (ids: string[], status: "active" | "frozen") => {
    const r = await runEach(ids, (id) => customersApi.update(id, { status }));
    if (r.failed) toast.error(`${fmtNum(r.ok)} עודכנו · ${fmtNum(r.failed)} לא עודכנו`);
    else toast.success(`${fmtNum(r.ok)} ${labels.customers} סומנו כ${STATUS_META[status]!.label}`);
    afterChange();
  };

  /** a new mailing to this audience: the selection (ids), or the status of the quick card (ADR-0033) */
  const mailTo = async (sel: CkSelection | null) => {
    if (sel && !sel.all) {
      navigate(`/mailing/new?customers=${sel.ids.join(",")}`);
      return;
    }
    if (sel?.all) {
      const ids = await table.resolveIds(sel);
      navigate(`/mailing/new?customers=${ids.join(",")}`);
      return;
    }
    navigate(`/mailing/new${STATUS_QUICK.has(quick) ? `?status=${quick}` : ""}`);
  };

  const closeCard = () => {
    setCardOpen(false);
    setSelectedCustomerId(null);
    if (searchParams.get("card")) {
      const next = new URLSearchParams(searchParams);
      next.delete("card");
      setSearchParams(next, { replace: true });
    }
  };

  const openImport = () => {
    setShowImport(true);
    resetImportDialog();
    setImportResult(null);
  };

  const resetImportDialog = () => {
    setSelectedFileName(null);
    setPreview(null);
    setImportError(null);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setCreateError(`יש להזין שם ${labels.customer}.`);
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const item = await customersApi.create({
        name: name.trim(),
        communityName: communityName.trim() || undefined,
        cityKey: cityKey || undefined,
        billingMethodKey: billingMethodKey || undefined,
        joinDate: joinDate || undefined,
        contactFirst: contactFirst.trim() || undefined,
        contactLast: contactLast.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
      });
      setShowCreate(false);
      setName("");
      setCommunityName("");
      setCityKey("");
      setBillingMethodKey("");
      setJoinDate("");
      setContactFirst("");
      setContactLast("");
      setContactPhone("");
      reload();
      setSelectedCustomerId(item.id);
      setCardOpen(true);
    } catch (err) {
      setCreateError(toClientApiError(err).message);
    } finally {
      setCreating(false);
    }
  };

  const handleFileChosen = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) {
      return;
    }
    setPreviewing(true);
    setImportError(null);
    setPreview(null);
    setSelectedFileName(file.name);
    try {
      const next = await customersApi.previewImportFile(file);
      setPreview(next);
    } catch (err) {
      setImportError(toClientApiError(err).message);
      setSelectedFileName(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommitImport = async () => {
    if (!preview?.storageKey) {
      setImportError("יש לבחור קובץ קודם.");
      return;
    }
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await customersApi.commitImport(preview.storageKey);
      setImportResult(
        `נוצרו ${result.created} ${labels.customers}` +
          (result.contactsCreated > 0
            ? `, ${result.contactsCreated} אנשי קשר`
            : "") +
          `, דולגו ${result.skipped}` +
          (result.errors.length > 0
            ? ` (שגיאות בשורות: ${result.errors
                .slice(0, 15)
                .map((e) => e.line)
                .join(", ")}${result.errors.length > 15 ? "…" : ""})`
            : ""),
      );
      setShowImport(false);
      resetImportDialog();
      reload();
    } catch (err) {
      setImportError(toClientApiError(err).message);
    } finally {
      setImporting(false);
    }
  };

  // deep link from the global search / dashboards: /customers?card=<id>
  const cardParam = searchParams.get("card");
  useEffect(() => {
    if (!cardParam) return;
    setSelectedCustomerId(cardParam);
    setCardOpen(true);
  }, [cardParam]);

  useQuickCreate("customer", () => setShowCreate(true));
  useEffect(() => onEvent<{ entity?: string }>(EVENTS.dataChanged, (d) => {
    if (d?.entity === "customer") reload();
  }), [reload]);
  useScreenSettings([
    { key: "customers-import", title: `ייבוא ${labels.customers} מקובץ`, description: "Excel או CSV, עם מיפוי עמודות וזיהוי כפילויות", icon: "upload", onClick: () => openImport() },
  ]);

  const rowMenu = (r: Customer): CkMenuItem[] => {
    const items: CkMenuItem[] = [{ key: "open", label: "פתיחת כרטיס", icon: "expand", kbd: "Enter", onClick: () => openCustomer(r.id) }];
    const phone = r.contactPhone || r.phone;
    if (phone) {
      items.push({
        key: "copy",
        label: "העתקת טלפון",
        icon: "copy",
        onClick: () =>
          void navigator.clipboard
            ?.writeText(phone)
            .then(() => toast("הועתק"))
            .catch(() => toast.error("ההעתקה נחסמה בדפדפן")),
      });
    }
    if (canMail && r.status !== "cancelled") {
      items.push({ key: "mail", label: "דיוור", icon: "mail", onClick: () => navigate(`/mailing/new?customers=${r.id}`) });
    }
    if (canUpdate && canCancelCustomerStatus(r.status)) {
      items.push(
        r.status === "frozen"
          ? { key: "activate", label: "סימון כפעיל", icon: "check", onClick: () => void setStatusMany([r.id], "active") }
          : { key: "freeze", label: "הקפאה", icon: "pause", onClick: () => void setStatusMany([r.id], "frozen") },
      );
    }
    if (canArchive && canCancelCustomerStatus(r.status)) {
      items.push(
        { divider: true },
        { key: "cancel", label: `ביטול ${labels.customer}`, icon: "archive", danger: true, onClick: () => setArchiveTarget({ ids: [r.id], label: r.name }) },
      );
    }
    return items;
  };

  const bulkActions = [
    ...(canMail ? [{ key: "mailing", label: "שליחת דיוור", icon: "mail", onClick: async (sel: CkSelection) => mailTo(sel) }] : []),
    ...(canUpdate
      ? [
          { key: "activate", label: "סימון כפעיל", icon: "check", onClick: async (sel: CkSelection) => setStatusMany(await table.resolveIds(sel), "active") },
          { key: "freeze", label: "הקפאה", icon: "pause", onClick: async (sel: CkSelection) => setStatusMany(await table.resolveIds(sel), "frozen") },
        ]
      : []),
    ...(canArchive
      ? [
          {
            key: "cancel",
            label: "ביטול",
            icon: "archive",
            danger: true,
            onClick: async (sel: CkSelection) => {
              const ids = await table.resolveIds(sel);
              setArchiveTarget({ ids, label: `${fmtNum(ids.length)} ${labels.customers}` });
            },
          },
        ]
      : []),
  ];

  const today = new Date().toISOString().slice(0, 10);
  const header = (
    <ListPageHeader
      screen
      sub={data ? `${fmtNum(data.quickCounts?.all ?? data.total)} ${labels.customers} · לחיצה על שורה פותחת את הכרטיס` : undefined}
      onRefresh={reload}
      primaryLabel={labels.customerNew}
      onPrimary={() => setShowCreate(true)}
      more={[
        { key: "import", label: "ייבוא מקובץ", icon: "upload", onClick: openImport },
        ...(canMail
          ? [
              {
                key: "mailing",
                label: STATUS_QUICK.has(quick) ? "דיוור ללקוחות בסינון" : "דיוור ללקוחות",
                icon: "mail",
                // the mailing audience uses the same filters as this list (ADR-0033)
                onClick: () => void mailTo(null),
              },
            ]
          : []),
      ]}
    />
  );

  return (
    <section className="page-section page-section--list">
      {header}

      {importResult ? <FormAlert severity="success">{importResult}</FormAlert> : null}

      {forbidden ? (
        <UiStateMessage state="permission" />
      ) : !data ? (
        // the columns come with the first page (product / custom-field columns differ per tenant)
        <UiStateMessage state={table.error ? "error" : "loading"} errorMessage={table.error ?? undefined} />
      ) : (
        <>
          <QuickFilters items={quickItems} value={quick || "all"} onChange={changeQuick} loading={table.loading && !qc} />
          {table.error ? <Banner tone="danger">{table.error}</Banner> : null}
          <DataTable<Customer>
            mode="server"
            columns={columns}
            rows={table.rows}
            total={table.total}
            loading={table.loading}
            onRetry={reload}
            onRefresh={reload}
            query={query}
            onQueryChange={setQuery}
            storageKey={TABLE_KEY}
            layoutDefaults={{ hidden: hiddenByDefault, pinned: ["name"] }}
            colSets={colSets}
            getValueOptions={table.getValueOptions}
            searchPlaceholder="חיפוש לפי שם, קוד, עיר, טלפון, איש קשר…"
            extraChips={extraChips}
            onClearAll={() => changeQuick("all")}
            builtinViews={builtinViews}
            getExtraViewState={() => quick}
            onApplyView={(x) => {
              const v = typeof x === "string" ? x : "";
              setQuick(isQuick(v) ? v : "");
            }}
            footerTotals={footerTotals}
            activeRowId={cardOpen ? selectedCustomerId : null}
            onRowClick={(r) => openCustomer(r.id)}
            rowActions={(r) => [{ key: "open", icon: "expand", label: "פתיחת כרטיס", onClick: () => openCustomer(r.id) }]}
            rowMenu={rowMenu}
            bulkActions={bulkActions}
            clearSelectionKey={clearKey}
            exportFilename={`${labels.customers}-${today}`}
            exportNote="סכומים בשקלים. כשיש בחירה — רק הנבחרים; אחרת כל התוצאות של הסינון."
            onExport={async ({ format, columns: cols, selection }) => {
              const list = await table.resolveRows(selection);
              await exportRows(list, cols, { format, filename: `${labels.customers}-${today}` });
              toast.success(`יוצאו ${fmtNum(list.length)} שורות`);
            }}
            emptyTitle={filtering ? `אין ${labels.customers} שמתאימים לסינון` : `עדיין אין ${labels.customers}`}
            emptyText={filtering ? "נסו לנקות את הסינון או לבחור כרטיס אחר." : "צרו את הראשון או ייבאו מקובץ."}
            renderMobileRow={(r) => (
              <>
                <b>{displayName(r)}</b>
                <small>{[lookupLabel("cityKey", r.cityKey), r.contactName, r.contactPhone, statusLabel(r.status)].filter(Boolean).join(" · ")}</small>
              </>
            )}
          />
        </>
      )}

      <CustomerCard
        open={cardOpen}
        customerId={selectedCustomerId}
        onClose={closeCard}
        onChanged={reload}
      />

      <ConfirmDialog
        open={Boolean(archiveTarget)}
        onClose={() => {
          if (!archiveBusy) {
            setArchiveTarget(null);
            setArchiveCancelReasonKey("");
          }
        }}
        title={`ביטול ${archiveTarget && archiveTarget.ids.length > 1 ? labels.customers : labels.customer}`}
        confirmText="ביטול"
        cancelText="חזרה"
        loading={archiveBusy}
        onConfirm={() => {
          void handleArchiveConfirm();
        }}
        onCancel={() => {
          setArchiveTarget(null);
          setArchiveCancelReasonKey("");
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0 }}>
            {archiveTarget
              ? `לבטל את "${archiveTarget.label}"? הסטטוס יעבור למבוטל.`
              : ""}
          </p>
          <LookupSelect
            label="סיבת ביטול"
            variant="fixed"
            value={archiveCancelReasonKey}
            onChange={setArchiveCancelReasonKey}
            options={cancelReasonOptions}
            disabled={archiveBusy}
            allowEmpty
            emptyLabel="בחרו סיבה…"
            required
          />
        </div>
      </ConfirmDialog>

      <FormDialog
        open={showCreate}
        onClose={() => {
          if (!creating) {
            setShowCreate(false);
            setCreateError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title={`יצירת ${labels.customer}`}
          error={createError}
          onSave={() => {
            void handleCreate();
          }}
          onCancel={() => {
            setShowCreate(false);
            setCreateError(null);
          }}
          saveLabel="יצירה"
          cancelLabel="ביטול"
          saving={creating}
          saveDisabled={!name.trim()}
        >
          <FormSection>
            <FormGrid cols={2}>
              <FormField
                label={`שם ${labels.customer}`}
                required
                value={name}
                onChange={setName}
                disabled={creating}
              />
              <FormField
                label="שם הקהילה"
                value={communityName}
                onChange={setCommunityName}
                disabled={creating}
              />
              <LookupSelect
                label="עיר"
                value={cityKey}
                onChange={setCityKey}
                options={cityOptions}
                disabled={creating}
                placeholder="חיפוש עיר"
                noOptionsText="אין ערים"
              />
              <LookupSelect
                label="אמצעי גבייה"
                variant="fixed"
                value={billingMethodKey}
                onChange={setBillingMethodKey}
                options={billingMethodOptions}
                disabled={creating}
              />
              <FormField
                label="תאריך הצטרפות"
                type="date"
                value={joinDate}
                onChange={setJoinDate}
                disabled={creating}
              />
              <FormField
                label="שם פרטי — איש קשר"
                value={contactFirst}
                onChange={setContactFirst}
                disabled={creating}
              />
              <FormField
                label="משפחה — איש קשר"
                value={contactLast}
                onChange={setContactLast}
                disabled={creating}
              />
              <FormField
                label="פלאפון"
                value={contactPhone}
                onChange={setContactPhone}
                disabled={creating}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <FormDialog
        open={showImport}
        onClose={() => {
          if (!importing && !previewing) {
            setShowImport(false);
            resetImportDialog();
          }
        }}
        size="lg"
      >
        <FormCard
          title={`ייבוא ${labels.customers} מקובץ`}
          error={importError}
          onSave={() => {
            void handleCommitImport();
          }}
          onCancel={() => {
            setShowImport(false);
            resetImportDialog();
          }}
          saveLabel="אישור ייבוא"
          cancelLabel="סגירה"
          saving={importing || previewing}
          saveDisabled={!preview || preview.estimatedCreate === 0 || importing}
        >
          <FormSection>
            <ol className="import-wizard-steps" aria-label="שלבי ייבוא">
              <li className={!preview ? "import-wizard-steps__active" : undefined}>
                1. העלאה
              </li>
              <li className={preview && !importing ? "import-wizard-steps__active" : undefined}>
                2. מיפוי ותצוגה מקדימה
              </li>
              <li className={importing ? "import-wizard-steps__active" : undefined}>
                3. אישור וסיכום
              </li>
            </ol>
            {!preview && !importing ? (
              <DataEntryRecommendation
                importType="customers"
                onLeave={() => {
                  setShowImport(false);
                  resetImportDialog();
                }}
              />
            ) : null}
            <FormGrid cols={1}>
              <label className="file-import-picker">
                <span className="file-import-picker__label">בחירת קובץ Excel או CSV</span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  disabled={importing || previewing}
                  onChange={(event) => {
                    void handleFileChosen(event.target.files);
                    event.target.value = "";
                  }}
                />
                <span className="file-import-picker__hint">
                  עמודות נתמכות: שם / name, טלפון, אימייל, כתובת, איש קשר
                  {selectedFileName ? ` · נבחר: ${selectedFileName}` : ""}
                </span>
              </label>

              {preview ? (
                <>
                  <FormAlert severity="info">
                    {preview.totalRows} שורות בקובץ · צפויים להיווצר{" "}
                    {preview.estimatedCreate} · לדלג {preview.estimatedSkip}
                    {preview.unmappedHeaders.length > 0
                      ? ` · עמודות שלא מופו: ${preview.unmappedHeaders.join(", ")}`
                      : ""}
                  </FormAlert>
                  {preview.errors.length > 0 ? (
                    <FormAlert severity="warning">
                      שגיאות לדוגמה:{" "}
                      {preview.errors
                        .slice(0, 8)
                        .map((e) => `שורה ${e.line}: ${e.message}`)
                        .join(" · ")}
                    </FormAlert>
                  ) : null}
                  <div className="file-import-preview">
                    <table>
                      <thead>
                        <tr>
                          <th>שורה</th>
                          <th>שם</th>
                          <th>טלפון</th>
                          <th>אימייל</th>
                          <th>איש קשר</th>
                          <th>סטטוס</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.previewRows.map((row) => (
                          <tr key={row.line}>
                            <td>{row.line}</td>
                            <td>{row.name || "—"}</td>
                            <td>{row.phone || "—"}</td>
                            <td>{row.email || "—"}</td>
                            <td>{row.contactName || "—"}</td>
                            <td>
                              {row.status === "ok" ? "תקין" : row.message ?? "שגיאה"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </section>
  );
}
